import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
import { NETWORK_ICON_MAP } from "../utils/networkConstants";
export const PLATFORM_LABELS = {
    adword: 'Adword',
    ga4: 'GA4',
    meta: 'Facebook Ads',
    bing: 'Bing Ads',
    criteo: 'Criteo',
    monday: 'Monday Setting',
    service_account: 'Service Account',
    google_spreadsheet: 'Google Spreadsheet',
    hubspot: 'Hubspot',
    quickbook: 'QuickBook Settings',
    shopify: 'Shopify',
    costingtons: 'Costingtons',
    semrush: 'Semrush',
};
export const PLATFORM_ICONS = {
    adword: NETWORK_ICON_MAP.adword,
    ga4: NETWORK_ICON_MAP.ga,
    meta: NETWORK_ICON_MAP.meta,
    bing: NETWORK_ICON_MAP.bing,
    criteo: NETWORK_ICON_MAP.criteo,
    monday: '/assets/monday.svg',
    shopify: NETWORK_ICON_MAP.shopify,
    hubspot: '/assets/hubspot.svg',
    google_spreadsheet: '/assets/google_sheets.svg',
    quickbook: '/assets/quickbooks.svg',
    costingtons: '/assets/costingtons.svg',
    semrush: '/assets/semrush.svg',
};
export const PLATFORM_FIELDS = {
    adword: ['account', 'client_id', 'client_secret', 'developer_token', 'mcc_id', 'refresh_token'],
    ga4: ['service_account_credentials'],
    meta: ['account', 'access_token'],
    bing: ['account', 'customer_id', 'client_id', 'client_secret', 'refresh_token', 'developer_token'],
    criteo: ['account', 'client_id', 'client_secret', 'version'],
    monday: ['account', 'access_token', 'active_client_list_board_id'],
    service_account: ['account', 'email'],
    google_spreadsheet: [
        'account',
        'paid_media_projection_sheet_id',
        'affiliate_projection_sheet_id',
        'paid_media_commission_sheet_id',
        'affiliate_commission_sheet_id',
        'opted_out_contact_sheet_id',
    ],
    hubspot: ['account', 'access_token'],
    quickbook: ['account', 'client_id', 'client_secret', 'company_id'],
    shopify: ['account', 'version'],
    costingtons: ['account', 'username', 'password'],
    semrush: ['account', 'api_key'],
};
export const PLATFORMS = Object.keys(PLATFORM_FIELDS);
// INITIAL STATE
const initialState = {
    records: [],
    loading: false,
    error: null,
    saving: null,
    saveError: null,
    deleting: false,
    deleteError: null,
    hubspotOwners: [],
    hubspotPortalUsers: [],
    hubspotUsersLoading: false,
    hubspotUsersError: null,
    hubspotUsersSaving: null,
};
// ASYNC THUNKS
export const fetchAllCredentials = createAsyncThunk("integrationCredentials/fetchAll", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("GET", "/api/integration-credentials", undefined, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "integration_credentials",
        });
        if (response && "data" in response && response.data?.success) {
            return response.data.data || [];
        }
        return rejectWithValue(response?.data?.message || "Failed to fetch credentials");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to fetch credentials");
    }
});
export const upsertCredentials = createAsyncThunk("integrationCredentials/upsert", async ({ platform, credentials }, { getState, rejectWithValue, dispatch }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("PUT", `/api/integration-credentials/${platform}`, { credentials }, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "integration_credentials",
        });
        if (response && "data" in response && response.data?.success) {
            toast.success("Credentials saved successfully");
            dispatch(fetchAllCredentials());
            return response.data.data;
        }
        const errorMsg = response?.data?.message || "Failed to save credentials";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to save credentials";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
export const deleteCredentials = createAsyncThunk("integrationCredentials/delete", async (platform, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("DELETE", `/api/integration-credentials/${platform}`, null, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "integration_credentials",
        });
        if (response && "data" in response && response.data?.success) {
            toast.success("Credentials deleted successfully");
            return platform;
        }
        const errorMsg = response?.data?.message || "Failed to delete credentials";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
    catch (error) {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete credentials";
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
    }
});
export const fetchHubspotUsers = createAsyncThunk("integrationCredentials/fetchHubspotUsers", async (_, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("GET", "/api/integration-credentials/hubspot/users", undefined, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "integration_credentials",
        });
        if (response && "data" in response && response.data?.success) {
            return response.data.data;
        }
        return rejectWithValue(response?.data?.message || "Failed to fetch HubSpot users");
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message || error?.message || "Failed to fetch HubSpot users");
    }
});
export const assignPortalUser = createAsyncThunk("integrationCredentials/assignPortalUser", async ({ hubspot_owner_id, user_id, hubspot_user_id, hubspot_username }, { getState, rejectWithValue }) => {
    try {
        const token = getState().auth.token;
        const response = await ApiCall("PUT", `/api/integration-credentials/hubspot/users/${hubspot_owner_id}`, { user_id, hubspot_user_id, hubspot_username }, {
            Authorization: `Bearer ${token}`,
            "x-module-key": "integration_credentials",
        });
        if (response && "data" in response && response.data?.success) {
            toast.success(user_id ? "Portal user assigned successfully" : "Portal user removed successfully");
            return response.data.data;
        }
        const fallbackMsg = user_id ? "Failed to assign portal user" : "Failed to remove portal user";
        const msg = response?.data?.message || fallbackMsg;
        toast.error(msg);
        return rejectWithValue(msg);
    }
    catch (error) {
        const fallbackMsg = user_id ? "Failed to assign portal user" : "Failed to remove portal user";
        const msg = error?.response?.data?.message || error?.message || fallbackMsg;
        toast.error(msg);
        return rejectWithValue(msg);
    }
});
// SLICE
const integrationCredentialsSlice = createSlice({
    name: "integrationCredentials",
    initialState,
    reducers: {
        clearIntegrationErrors: (state) => {
            state.error = null;
            state.saveError = null;
            state.deleteError = null;
        },
        clearIntegrationRecords: (state) => {
            state.records = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAllCredentials.pending, (state) => {
            state.loading = true;
            state.error = null;
        })
            .addCase(fetchAllCredentials.fulfilled, (state, action) => {
            state.loading = false;
            state.records = action.payload;
        })
            .addCase(fetchAllCredentials.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload || "Failed to fetch credentials";
        });
        builder
            .addCase(upsertCredentials.pending, (state, action) => {
            state.saving = action.meta.arg.platform;
            state.saveError = null;
        })
            .addCase(upsertCredentials.fulfilled, (state) => {
            state.saving = null;
            state.saveError = null;
        })
            .addCase(upsertCredentials.rejected, (state, action) => {
            state.saving = null;
            state.saveError = action.payload || "Failed to save credentials";
        });
        builder
            .addCase(deleteCredentials.pending, (state) => {
            state.deleting = true;
            state.deleteError = null;
        })
            .addCase(deleteCredentials.fulfilled, (state, action) => {
            state.deleting = false;
            state.records = state.records.filter((r) => r.platform !== action.payload);
        })
            .addCase(deleteCredentials.rejected, (state, action) => {
            state.deleting = false;
            state.deleteError = action.payload || "Failed to delete credentials";
        });
        builder
            .addCase(fetchHubspotUsers.pending, (state) => {
            state.hubspotUsersLoading = true;
            state.hubspotUsersError = null;
        })
            .addCase(fetchHubspotUsers.fulfilled, (state, action) => {
            state.hubspotUsersLoading = false;
            state.hubspotOwners = action.payload.owners;
            state.hubspotPortalUsers = action.payload.portal_users;
        })
            .addCase(fetchHubspotUsers.rejected, (state, action) => {
            state.hubspotUsersLoading = false;
            state.hubspotUsersError = action.payload || "Failed to fetch HubSpot users";
        });
        builder
            .addCase(assignPortalUser.pending, (state, action) => {
            state.hubspotUsersSaving = action.meta.arg.hubspot_owner_id;
        })
            .addCase(assignPortalUser.fulfilled, (state, action) => {
            state.hubspotUsersSaving = null;
            const updated = action.payload;
            const idx = state.hubspotOwners.findIndex((o) => o.hubspot_owner_id === updated.hubspot_owner_id);
            if (idx !== -1) {
                state.hubspotOwners[idx] = { ...state.hubspotOwners[idx], ...updated };
            }
        })
            .addCase(assignPortalUser.rejected, (state) => {
            state.hubspotUsersSaving = null;
        });
    },
});
// ACTIONS
export const { clearIntegrationErrors, clearIntegrationRecords } = integrationCredentialsSlice.actions;
// SELECTORS
export const selectIntegrationRecords = (state) => state.integrationCredentials.records;
export const selectIntegrationLoading = (state) => state.integrationCredentials.loading;
export const selectIntegrationError = (state) => state.integrationCredentials.error;
export const selectIntegrationSaving = (state) => state.integrationCredentials.saving;
export const selectIntegrationSaveError = (state) => state.integrationCredentials.saveError;
export const selectIntegrationDeleting = (state) => state.integrationCredentials.deleting;
export const selectIntegrationDeleteError = (state) => state.integrationCredentials.deleteError;
export const selectHubspotOwners = (state) => state.integrationCredentials.hubspotOwners;
export const selectHubspotPortalUsers = (state) => state.integrationCredentials.hubspotPortalUsers;
export const selectHubspotUsersLoading = (state) => state.integrationCredentials.hubspotUsersLoading;
export const selectHubspotUsersError = (state) => state.integrationCredentials.hubspotUsersError;
export const selectHubspotUsersSaving = (state) => state.integrationCredentials.hubspotUsersSaving;
export default integrationCredentialsSlice.reducer;
